import { forwardRef, useEffect, useMemo, useState } from "react"

import { faAngleDown } from "@fortawesome/pro-regular-svg-icons"
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Skeleton,
  Typography,
} from "@mui/material"
import {
  DateCalendar,
  PickersDay,
  PickersDayProps,
  PickerValidDate,
} from "@mui/x-date-pickers"
import { FaIcon } from "@skistarab/mui-theme"
import { format } from "date-fns"
import { bindToggle, usePopupState } from "material-ui-popup-state/hooks"
import { useTranslation } from "react-i18next"

import { useDateLocale } from "@/hooks"
import { formatStartDate } from "@/utils/dateUtils"

import CalendarPopover from "./components/CalendarPopover"
import {
  calendarIconStyles,
  dateDisplayStyles,
  getFormControlStyles,
  labelStyles,
} from "./styles"

type DateSelectorProps = {
  label: string
  selectedDate: Date
  isLoading?: boolean
  isLoadingDates?: boolean
  minDate?: Date
  maxDate?: Date
  unAvailableDates?: Date[]
  setSelectedDate: (date: Date) => void
  maxWidth?: string
  onCalendarOpen?: () => void
  onMonthChange?: (date: Date) => void
}

const DateSelector = ({
  label,
  selectedDate,
  isLoading,
  isLoadingDates,
  minDate,
  maxDate,
  unAvailableDates,
  setSelectedDate,
  maxWidth,
  onCalendarOpen,
  onMonthChange,
}: DateSelectorProps) => {
  const { t } = useTranslation()
  const locale = useDateLocale()

  const popupState = usePopupState({
    variant: "popover",
    popupId: "datePickerPopover",
  })

  const formattedDate = useMemo(() => {
    return formatStartDate(selectedDate, locale)
  }, [selectedDate, locale])

  const { calendarMinDate, calendarMaxDate } = useMemo(() => {
    const today = new Date()
    const oneYearFromNow = new Date(today)
    oneYearFromNow.setFullYear(today.getFullYear() + 1)
    return {
      calendarMinDate: minDate && minDate > today ? minDate : today,
      calendarMaxDate: maxDate ?? oneYearFromNow,
    }
  }, [minDate, maxDate])

  useEffect(() => {
    if (popupState.isOpen) {
      onCalendarOpen?.()
    }
  }, [onCalendarOpen, popupState.isOpen])

  const handleDateChange = (date: Date) => {
    setSelectedDate(date)
    popupState.close()
  }

  const checkDate = (date: PickerValidDate) => {
    if (!unAvailableDates || unAvailableDates.length === 0) return false

    const dateStr = format(date, "yyyy-MM-dd")
    return unAvailableDates.some(
      (unavailableDate) => format(unavailableDate, "yyyy-MM-dd") === dateStr
    )
  }

  if (isLoading) {
    return <Skeleton width="100%" height={56} sx={{ maxWidth }} />
  }

  return (
    <>
      <FormControl
        component="button"
        fullWidth
        sx={{ maxWidth, ...getFormControlStyles(popupState.isOpen) }}
        aria-describedby="startdate-sr-helper-text"
        aria-expanded={popupState.isOpen}
        {...bindToggle(popupState)}
      >
        <InputLabel component="div" shrink sx={labelStyles}>
          {label}
        </InputLabel>
        <Typography
          variant="body2"
          component="span"
          color="text.primary"
          sx={dateDisplayStyles}
          data-testid="date-selector"
          aria-label={`${t("selectedStartDate")}: ${formattedDate}`}
        >
          {formattedDate}
        </Typography>
        <Box
          sx={{
            ...calendarIconStyles,
            transform: popupState.isOpen ? "rotate(180deg)" : undefined,
          }}
        >
          <FaIcon
            icon={faAngleDown}
            data-testid="CalendarIcon"
            fontSize="inherit"
          />
        </Box>
      </FormControl>

      <CalendarPopover popupState={popupState}>
        <Box sx={{ position: "relative" }}>
          <CalendarContent
            defaultDate={selectedDate}
            onDateChange={handleDateChange}
            minDate={calendarMinDate}
            maxDate={calendarMaxDate}
            checkDate={checkDate}
            onMonthChange={onMonthChange}
          />
          {isLoadingDates && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255, 255, 255, 0.8)",
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>
      </CalendarPopover>
    </>
  )
}

const CustomDay = forwardRef<HTMLButtonElement, PickersDayProps>(
  ({ day, ...other }, ref) => {
    const locale = useDateLocale()
    const ariaLabel = format(day, "EEEE d MMMM yyyy", { locale })

    return <PickersDay {...other} day={day} aria-label={ariaLabel} ref={ref} />
  }
)

const CalendarContent = ({
  defaultDate,
  onDateChange,
  minDate,
  maxDate,
  checkDate,
  onMonthChange,
}: {
  defaultDate: Date
  onDateChange: (date: Date) => void
  minDate: Date
  maxDate: Date
  checkDate: (date: PickerValidDate) => boolean
  onMonthChange?: (date: Date) => void
}) => {
  const [tempDate, setTempDate] = useState<Date>(defaultDate)
  return (
    <DateCalendar
      displayWeekNumber
      value={tempDate}
      onChange={(date, selectionState) => {
        if (!date) return
        setTempDate(date)
        if (selectionState === "finish") {
          onDateChange(date)
        }
      }}
      minDate={minDate}
      maxDate={maxDate}
      shouldDisableDate={checkDate}
      onMonthChange={onMonthChange}
      views={["year", "month", "day"]}
      autoFocus
      slots={{
        day: CustomDay,
      }}
    />
  )
}

export default DateSelector
